import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { generatePicks, savePick, GAME_THEME } from "../lib/api";
import { Ball } from "./Ball";
import { Button } from "./ui/button";
import { Sparkles, Bookmark, Loader2, TriangleAlert } from "lucide-react";
import { toast } from "sonner";

export const AIPicksCard = ({ game, window, onSaved }) => {
  const t = GAME_THEME[game];
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const generate = async () => {
    setLoading(true);
    setResult(null);
    try {
      const data = await generatePicks(game, window);
      if (!data.sets?.length) {
        toast.error("The model returned no valid sets. Try again.");
      }
      setResult(data);
    } catch (e) {
      toast.error("AI generation failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const save = async (s) => {
    try {
      await savePick({
        game, numbers: s.numbers, special: s.special,
        strategy: s.strategy, rationale: s.rationale,
      });
      toast.success("Pick saved to favorites");
      onSaved?.();
    } catch {
      toast.error("Could not save pick");
    }
  };

  return (
    <div
      className={`rounded-2xl bg-zinc-900/60 border border-purple-500/30 p-6 sm:p-8 relative ${loading ? "ai-glow" : ""}`}
      data-testid="ai-picks-card"
    >
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Sparkles size={18} className="text-purple-400" />
            <h3 className="font-heading text-xl text-zinc-50">AI Number Suggestions</h3>
          </div>
          <p className="text-sm text-zinc-400">
            Blends hot, cold, overdue &amp; pair trends for the <span style={{ color: t.color }}>{t.name}</span> window you selected.
          </p>
        </div>
        <Button
          onClick={generate}
          disabled={loading}
          data-testid="generate-ai-picks"
          className="bg-purple-600 hover:bg-purple-500 text-white rounded-full px-6 h-11 font-semibold shrink-0 transition-colors duration-200"
        >
          {loading ? <><Loader2 size={16} className="mr-2 animate-spin" /> Generating…</> : <><Sparkles size={16} className="mr-2" /> Generate Picks</>}
        </Button>
      </div>

      <AnimatePresence mode="wait">
        {result?.sets?.length > 0 && (
          <motion.div
            key={result.sets.map((s) => s.numbers.join()).join("|")}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="grid grid-cols-1 lg:grid-cols-3 gap-4"
          >
            {result.sets.map((s, si) => (
              <div key={`${s.numbers.join("-")}-${s.special}`} data-testid={`ai-set-${si}`}
                className="rounded-xl bg-zinc-950/70 border border-zinc-800 p-4 flex flex-col">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-[10px] uppercase tracking-wider font-semibold text-purple-300 bg-purple-500/10 rounded-full px-2.5 py-1">
                    {s.strategy}
                  </span>
                  <button onClick={() => save(s)} data-testid={`save-set-${si}`}
                    className="text-zinc-500 hover:text-purple-300 transition-colors duration-200">
                    <Bookmark size={16} />
                  </button>
                </div>
                <div className="flex flex-wrap items-center gap-1.5 mb-3">
                  {s.numbers.map((n, ni) => (
                    <Ball key={n} n={n} size="md" animate delay={si * 0.15 + ni * 0.08} />
                  ))}
                  <span className="text-zinc-600 px-0.5">+</span>
                  <Ball n={s.special} special color={t.color} size="md" animate delay={si * 0.15 + 0.5} />
                </div>
                <p className="text-xs text-zinc-400 leading-relaxed mt-auto">{s.rationale}</p>
              </div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      <div className="flex items-start gap-2 mt-6 text-zinc-500" data-testid="ai-disclaimer">
        <TriangleAlert size={14} className="mt-0.5 shrink-0" />
        <p className="text-xs italic">
          {result?.disclaimer || "For entertainment only. Lottery draws are completely random — these suggestions are not predictions and do not improve your odds."}
        </p>
      </div>
    </div>
  );
};
